import {
  Event,
  EventCategory,
  BookedStatusEnum,
  EventSubCategory,
  CategoryEnum,
} from './eventTypes';
import {
  SET_EVENTS,
  SET_EVENT_BY_ID,
  EventThunkAction,
  SetEventsActionType,
  SET_EVENT_CATEGORIES,
  SET_EVENT_TOTAL_PAGES,
  SetEventByIdActionType,
  SET_EVENT_TOTAL_RESULTS,
  SET_EVENT_SUB_CATEGORIES,
  SetEventCategoriesActionType,
  SetEventTotalPagesActionType,
  SetEventTotalResultsActionType,
  SetEventSubCategoriesActionType,
} from './eventActionTypes';
import { EVENT_NAMESPACE } from './eventReducer';
import EventApi from '../../API/event/Event.api';
import {
  CreditBalanceDto,
  BookEventRequestDto,
} from '../../API/event/dto/requests/book-event.request.dto';
import { MyActivitiesType } from '../my-activities/myActivitiesTypes';
import { startLoaderThunk, stopLoaderThunk } from '../modal/modalActions';
import { NavigationEnum } from '../../navigation/vertical/Navigation.enum';
import { FetchMyActivitiesThunk } from '../my-activities/myActivitiesActions';
import { EventFiltersRequestDto } from '../../API/event/dto/requests/event-filters.request.dto';
import { setEventPriceFromMembership } from '../../utility/functions/setEventPriceFromMembership';
import { subscriptionsManagementSetUserSubscriptionsThunk } from '../SubscriptionsManagement/subscriptionsManagementActions';

export function setEventsAction(events: Event[]): SetEventsActionType {
  return {
    type: SET_EVENTS,
    payload: events,
  };
}

export function setEventTotalResultsAction(
  totalResults: number
): SetEventTotalResultsActionType {
  return {
    type: SET_EVENT_TOTAL_RESULTS,
    payload: totalResults,
  };
}

export function setEventTotalPagesAction(
  totalPages: number
): SetEventTotalPagesActionType {
  return {
    type: SET_EVENT_TOTAL_PAGES,
    payload: totalPages,
  };
}

export function setEventCategoriesAction(
  categories: EventCategory[]
): SetEventCategoriesActionType {
  return {
    type: SET_EVENT_CATEGORIES,
    payload: categories,
  };
}

export function setEventSubCategoriesAction(
  subCategories: EventSubCategory[]
): SetEventSubCategoriesActionType {
  return {
    type: SET_EVENT_SUB_CATEGORIES,
    payload: subCategories,
  };
}

export function setEventByIdAction(event: Event): SetEventByIdActionType {
  return {
    type: SET_EVENT_BY_ID,
    payload: event,
  };
}

export function UpdateEventsAfterBookingOrCancel(
  id: number,
  status: BookedStatusEnum
): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const event = await dispatch(eventManagementFetchEventByIdThunk(id));
    event.bookedStatus = status;

    const oldEvents = getState()[EVENT_NAMESPACE].events;

    const updatedEvents: Event[] = [];
    for (let i = 0; i < oldEvents.length; i++) {
      const currentEvent = oldEvents[i];
      if (currentEvent.id === id) {
        const membershipState =
          getState()['SUBSCRIPTIONS_MANAGEMENT_NAMESPACE'];
        const priced = setEventPriceFromMembership(
          [event],
          membershipState.subscriptionsMap
        );
        updatedEvents.push(priced[0]);
      } else {
        updatedEvents.push({ ...currentEvent });
      }
    }

    dispatch(setEventsAction(updatedEvents));
  };
}

export function FetchEventsFilteredThunk(
  filter: EventFiltersRequestDto,
  addEvents: boolean = false
): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const response = await EventApi.getFilteredEvents(filter);
    const { events, totalResults, totalPages } = response.data;

    let membershipState = getState()['SUBSCRIPTIONS_MANAGEMENT_NAMESPACE'];

    if (!membershipState.user.isInit) {
      // get memberships if not init
      await dispatch(subscriptionsManagementSetUserSubscriptionsThunk());
      membershipState = getState()['SUBSCRIPTIONS_MANAGEMENT_NAMESPACE'];
    }

    let pricedEvents: Event[] = setEventPriceFromMembership(
      events,
      membershipState.subscriptionsMap
    );

    if (addEvents) {
      const currentEvents = getState()[EVENT_NAMESPACE].events;
      pricedEvents = currentEvents.concat(events);
    }

    dispatch(setEventsAction(pricedEvents));
    dispatch(setEventTotalPagesAction(totalPages));
    dispatch(setEventTotalResultsAction(totalResults));
  };
}

export function FetchEventCategoriesThunk(): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    EventApi.getEventCategories()
      .then((response) => {
        dispatch(setEventCategoriesAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function FetchEventSubCategoriesByCategoryIdThunk(
  category: CategoryEnum
): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    EventApi.getEventSubCategoriesByCategoryId(category)
      .then((response) => {
        dispatch(setEventSubCategoriesAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function eventManagementFetchEventByIdThunk(
  id: number
): EventThunkAction<Promise<any>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return EventApi.getEventById(id)
      .then((response) => {
        return response.data;
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function eventManagementFetchValidEventByIdThunk(
  id: number
): EventThunkAction<Promise<any>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return EventApi.getValidEventById(id)
      .then((response) => {
        dispatch(setEventByIdAction(response.data));
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function BookEventThunk(
  event: Event,
  fromWhichPage: NavigationEnum,
  pendingPayment = false
): EventThunkAction<
  Promise<{
    isPaid: boolean;
    paymentProcessLink?: string;
    orderId: number;
    authCode?: string;
    authCodeExpiration?: string;
  }>
> {
  return async (dispatch, getState) => {
    const creditState = getState()['CREDITS_NAMESPACE'];
    const authState = getState()['AUTH_NAMESPACE'];
    const creditCardsState = getState()['CREDIT_CARDS_NAMESPACE'];

    await dispatch(startLoaderThunk()).finally();
    const status = pendingPayment
      ? BookedStatusEnum.PENDING_BOOKING
      : BookedStatusEnum.BOOKED;

    const newData: BookEventRequestDto = {
      user_id: authState.profile?.id!,
      event_id: event.id,
      company_id: authState.profile?.companyId,
      start_date: +event.fromDate,
      end_date: +event.toDate,
      price: +event.price,
      status,
    };

    const creditBalance: CreditBalanceDto = {
      credit_payments: creditState.creditInUse?.activeCredits?.map((credit) => {
        return {
          credit_balance_id: credit.id,
          credit_amount: credit.amount,
        };
      }),
      cash_amount: creditState.creditInUse?.cashAmount || null,
      credit_card_id: creditCardsState.selectedCreditCard?.id,
      save_credit_card: creditCardsState.saveCreditCard,
    };

    try {
      const response = await EventApi.bookEvent(
        newData,
        creditBalance,
        event.bookedStatus === BookedStatusEnum.PENDING_BOOKING
      );
      if (
        fromWhichPage === NavigationEnum.BOOK_A_FITNESS ||
        fromWhichPage === NavigationEnum.EVENTS
      ) {
        await dispatch(UpdateEventsAfterBookingOrCancel(event.id, status));
      } else if (fromWhichPage === NavigationEnum.MY_ACTIVITIES) {
        await dispatch(FetchMyActivitiesThunk(MyActivitiesType.FUTURE, 12));
      }
      return {
        isPaid: response.data.paymentsProcess.isPaid,
        authCode: response.data.paymentsProcess.authCode,
        authCodeExpiration: response.data.paymentsProcess.authCodeExpiration,
        paymentProcessLink: response.data.paymentsProcess.paymentProcessLink,
        orderId: response.data.id,
      };
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function StartOrderCancellationProcessThunk(
  eventId: number,
  orderId: number
): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    await dispatch(startLoaderThunk());
    await EventApi.startOrderCancellationProcess(orderId);
    await dispatch(
      UpdateEventsAfterBookingOrCancel(
        eventId,
        BookedStatusEnum.PENDING_BOOKING
      )
    );
    await dispatch(stopLoaderThunk());
  };
}

export function JoinWaitingListThunk(
  event: Event
): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const creditState = getState()['CREDITS_NAMESPACE'];
    const authState = getState()['AUTH_NAMESPACE'];
    const creditCardsState = getState()['CREDIT_CARDS_NAMESPACE'];

    dispatch(startLoaderThunk()).finally();

    const newData: BookEventRequestDto | any = {
      user_id: authState.profile?.id,
      event_id: event?.id,
      start_date: +event.fromDate,
      end_date: +event.toDate,
      price: +event.price,
      status: BookedStatusEnum.IN_WAIT_LIST,
    };

    const creditBalance = {
      credit_payments: creditState.creditInUse?.activeCredits?.map((credit) => {
        return {
          credit_balance_id: credit.id,
          credit_amount: credit.amount,
        };
      }),
      credit_amount: creditState.creditInUse?.creditsAmount || null,
      cash_amount: creditState.creditInUse?.cashAmount || null,
      credit_card_id: creditCardsState.selectedCreditCard?.id,
    };

    try {
      await EventApi.bookEvent(newData, creditBalance);
      await dispatch(
        UpdateEventsAfterBookingOrCancel(
          event.id,
          BookedStatusEnum.IN_WAIT_LIST
        )
      );
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function RemoveBookedEventThunk(
  eventId: number,
  fromWhichPage: NavigationEnum
): EventThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    try {
      await EventApi.cancelBooking(eventId);

      if (
        fromWhichPage === NavigationEnum.BOOK_A_FITNESS ||
        fromWhichPage === NavigationEnum.EVENTS
      ) {
        await dispatch(
          UpdateEventsAfterBookingOrCancel(eventId, BookedStatusEnum.AVAILABLE)
        );
      } else if (
        fromWhichPage === NavigationEnum.MY_ACTIVITIES ||
        fromWhichPage === NavigationEnum.DASHBOARDS
      ) {
        await dispatch(FetchMyActivitiesThunk(MyActivitiesType.FUTURE, 12));
      }
    } finally {
      dispatch(stopLoaderThunk()).finally();
    }
  };
}
